import React, { FC, ReactNode, useEffect } from 'react'

import { useIntl } from 'react-intl'

import { Button, Typography, useTheme } from '@themenu/design-system'

import { PageTitle } from '@app/components/Shared/PageTitle'
import { useLogger } from '@app/hooks/useLogger'
import { InvitationContentLayout, InvitationFlowLayout } from '@app/layout/InvitationLayout'
import { ApiError } from '@app/types/invitations'

type ErrorPageProps = {
  error: Error | ApiError | null
  origin: string
}

const isApiError = (error: Error | ApiError | null): error is ApiError => {
  return error !== null && 'error' in error
}

export const ErrorPage: FC<ErrorPageProps> = ({ origin, error }) => {
  const { formatMessage } = useIntl()
  const theme = useTheme()
  const { logger } = useLogger()

  useEffect(() => {
    logger({
      level: 'error',
      message: 'Generic error page',
      context: {
        origin,
        error: isApiError(error) ? error?.error?.message : error?.message,
        status: isApiError(error) ? error?.status : '',
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <InvitationFlowLayout showBackButton={false}>
      <PageTitle title={formatMessage({ id: 'error_page.page_title' })} />
      <div className="flex w-full flex-1 flex-col justify-between gap-24 px-20 pb-32 xs:flex-none xs:px-0 xs:pb-0">
        <InvitationContentLayout>
          <div>
            <div className="mb-24">
              <Typography variant="largeTitle">
                {formatMessage(
                  { id: 'error_page.new_title' },
                  {
                    strong: (text: ReactNode) => (
                      <Typography variant="largeTitle" component="span" color={theme.colors.title.red} display="inline">
                        {text}
                      </Typography>
                    ),
                  },
                )}
              </Typography>
            </div>
            <div className="w-full">
              <Typography variant="body">{formatMessage({ id: 'error_page.subtitle' })}</Typography>
            </div>
          </div>
        </InvitationContentLayout>
        <div className="flex flex-col gap-8">
          <Button component="a" href="https://help.swile.co/hc/en-gb" size={48} width="100%">
            {formatMessage({
              id: 'error_page.contact_us',
            })}
          </Button>
          <Button component="a" href="/" variant="alternativeText" size={48} width="100%">
            {formatMessage({
              id: 'error_page.back',
            })}
          </Button>
        </div>
      </div>
    </InvitationFlowLayout>
  )
}
